function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InputField from "../InputField/InputField";
import Icon from "../../Display/Icon/Icon";
import Places from "../../utils/Places";
import { stringifyAddress, updateFieldsValues } from "./utils";
import isAddressValid from "./utils/isAddressValid";

/** Field keys expected by the LocationAddressField component. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const fieldNames = ['line_1', 'city', 'county', 'state', 'postal_code', 'country', 'latitude', 'longitude'];
function getCounty(place) {
  return place.address_components.find(component => component.types.includes('administrative_area_level_2'));
}
function getComputedProperty(obj, prop) {
  let value = obj[prop];
  if (typeof value === 'function') {
    value = value();
  }
  return value;
}

/**
 * Helper hook to setup the fields required by the LocationAddressField component.
 *
 * Only works with react-final-form.
 * You can pass the resulting object to the LocationAddressField component.
 *
 * Note: The useField hook is passed through dependency injection to avoid some
 * dependency issues with react-final-form.
 *
 * @param {Function} useField The useField hook from react-final-form.
 * @param {Object} [configs] An object with the field names as keys and the field configs as values.
 * @returns {Object} An object with the field names as keys and the field objects as values.
 */
export function useLocationAddressFields(useField) {
  let configs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return Object.fromEntries(fieldNames.map(fn => {
    const config = configs[fn] || {};
    return [fn, useField(fn, config)];
  }));
}
const LocationAddressField = props => {
  const {
    cityRequiredMessage = 'City is required',
    className = '',
    coreApiClientBase = null,
    dataTestId = '',
    fieldNamePath = 'address',
    google,
    id,
    initialTouched = false,
    label,
    placeholder = '',
    required = true,
    requiredMessage = 'Required'
  } = props;
  const addressData = fieldNamePath ? props[fieldNamePath] : props;
  const invalidAddressMsg = cityRequiredMessage;
  const isValid = isAddressValid(addressData, required);
  const [address, setAddress] = useState({
    name: id || 'pseudo-address',
    value: stringifyAddress(addressData),
    valid: isValid,
    invalid: !isValid,
    visited: false,
    active: false,
    touched: initialTouched,
    dirty: false,
    pristine: true,
    error: isValid ? undefined : invalidAddressMsg
  });
  useEffect(() => {
    setAddress(_objectSpread(_objectSpread({}, address), {}, {
      value: stringifyAddress(addressData),
      valid: isValid,
      invalid: !isValid
    }));
  }, [addressData.line_1, addressData.city, addressData.county, addressData.country, addressData.postal_code, addressData.state]);
  const [dropdown, setDropdown] = useState({
    open: false,
    results: []
  });
  const placesAdapters = {
    google,
    coreApiClientBase
  };
  const autocomplete = input => Places.autocomplete(_objectSpread({
    input
  }, placesAdapters));
  const debouncedAutocomplete = input => Places.debouncedAutocomplete(_objectSpread({
    input
  }, placesAdapters));
  const getPlaceDetails = placeId => Places.getPlaceDetails(_objectSpread({
    placeId
  }, placesAdapters));
  const onAddressChange = async event => {
    updateFieldsValues(null, addressData);
    const {
      value
    } = event.target;
    // timeout set to push setAddress value AFTER useEffect hook triggered by updateFieldsValues
    setTimeout(() => {
      setAddress(prevAddress => _objectSpread(_objectSpread({}, prevAddress), {}, {
        value,
        valid: false,
        invalid: true,
        pristine: false,
        dirty: true,
        touched: true,
        error: invalidAddressMsg
      }));
    }, 0);
    try {
      const results = await debouncedAutocomplete(value);
      setDropdown({
        open: true,
        results
      });
    } catch (error) {
      setDropdown({
        open: true,
        results: []
      });
      console.error(error);
    }
  };
  const onAddressFocus = () => {
    setAddress(prevAddress => _objectSpread(_objectSpread({}, prevAddress), {}, {
      active: true,
      touched: address.dirty
    }));
    if (dropdown.results.length !== 0) {
      setDropdown(_objectSpread(_objectSpread({}, dropdown), {}, {
        open: true
      }));
    }
  };
  const onAddressBlur = event => {
    setAddress(_objectSpread(_objectSpread(_objectSpread({}, address), !event.target.value ? {
      error: required ? requiredMessage : undefined,
      valid: !required,
      invalid: required
    } : {}), {}, {
      active: false,
      visited: true,
      touched: true,
      pristine: false
    }));
  };
  const onResultClick = result => async () => {
    let place = await getPlaceDetails(result.place_id);
    if (!getCounty(place)) {
      try {
        const newResults = await autocomplete(place.formatted_address);
        const newDetailedPlaces = await Promise.all(newResults.map(newResult => getPlaceDetails(newResult.place_id)));
        const newCounty = newDetailedPlaces.map(getCounty).find(county => county);
        if (newCounty) {
          place = _objectSpread(_objectSpread({}, place), {}, {
            address_components: [...place.address_components, newCounty]
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    const {
      location
    } = place.geometry;
    const resultObj = _objectSpread(_objectSpread({}, result), {}, {
      result: _objectSpread({}, place),
      latLng: {
        lat: getComputedProperty(location, 'lat'),
        lng: getComputedProperty(location, 'lng')
      }
    });
    updateFieldsValues(resultObj, addressData);
    setDropdown(_objectSpread(_objectSpread({}, dropdown), {}, {
      open: false
    }));
  };
  const onResultKeyDown = result => event => {
    // Capture Enter and Space keys
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      onResultClick(result)();
    }
  };
  const onCloseDropDown = () => {
    setDropdown(_objectSpread(_objectSpread({}, dropdown), {}, {
      open: false
    }));
  };
  return /*#__PURE__*/_jsx("div", {
    className: className,
    children: /*#__PURE__*/_jsxs("div", {
      id: "".concat(id, "-address"),
      className: "relative",
      children: [/*#__PURE__*/_jsx(InputField, _objectSpread(_objectSpread({
        id: "".concat(id, "-address-field"),
        label: label
      }, address), {}, {
        onChange: onAddressChange,
        onFocus: onAddressFocus,
        onBlur: onAddressBlur,
        required: required,
        placeholder: placeholder,
        dataTestId: dataTestId,
        hideError: dropdown.open
      })), dropdown.open && /*#__PURE__*/_jsx(Icon, {
        id: "address-dropdown-close-btn",
        icon: "Times",
        onClick: onCloseDropDown,
        className: "absolute top-1/2 right-0 -mt-1px mr-2",
        size: 16
      }), dropdown.open && /*#__PURE__*/_jsxs("div", {
        className: "absolute top-10 -mt-1 bg-white border rounded border-solid border-light-blue-border z-50 w-full",
        children: [dropdown.results.map(result => /*#__PURE__*/_jsx("button", {
          className: "p-4 border-b border-light-blue-border last:border-0 w-full",
          role: "button",
          onClick: onResultClick(result),
          onKeyDown: onResultKeyDown(result),
          tabIndex: "0",
          children: /*#__PURE__*/_jsxs("div", {
            className: "flex text-text-blue",
            children: [/*#__PURE__*/_jsx(Icon, {
              icon: "MapPin",
              color: "#4467AB",
              className: "mr-4 my-auto"
            }), result.description]
          })
        }, result.place_id)), dropdown.results.length === 0 && /*#__PURE__*/_jsx("div", {
          className: "cursor-default p-4 text-text-blue",
          children: "No matches found"
        })]
      })]
    })
  });
};
LocationAddressField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  /** label text for button */
  label: PropTypes.string.isRequired,
  /** path to the address object from the Fields names */
  fieldNamePath: PropTypes.string,
  /** the google maps object */
  google: PropTypes.object.isRequired,
  /** pass required to InputField for address */
  required: PropTypes.bool,
  // Make zip code a required field.
  initialTouched: PropTypes.bool,
  cityRequiredMessage: PropTypes.string,
  requiredMessage: PropTypes.string,
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
  coreApiClientBase: PropTypes.object
};
export default LocationAddressField;
//# sourceMappingURL=LocationAddressField.js.map